import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Button, StatCard, Table, useTable } from "@repo/o37-ui";
import { Plus, Building, ChevronRight, Users } from "lucide-react";
import { HoldingsCompany, HOLDINGS_COMPANY_STATUSES } from '../../types';
import CompanyForm from '../../components/Holdings/CompanyForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function CompanyList() {
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<HoldingsCompany | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        const transformed: HoldingsCompany[] = data.map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status,
          ownershipStakePercentage: c.ownership_stake_percentage,
          description: c.description,
          website: c.website,
          logoUrl: c.logo_url,
          industry: c.industry,
          foundedDate: c.founded_date,
          acquisitionDate: c.acquisition_date,
          exitDate: c.exit_date,
          notes: c.notes,
          createdBy: c.created_by,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
          people: c.holdings_company_people || []
        }));
        // Sort: active first, then by name
        transformed.sort((a, b) => {
          if (a.status === 'active' && b.status !== 'active') return -1;
          if (a.status !== 'active' && b.status === 'active') return 1;
          return a.name.localeCompare(b.name);
        });
        setCompanies(transformed);
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const found = HOLDINGS_COMPANY_STATUSES.find(s => s.status === status);
    return found?.color || 'bg-neutral-100 text-neutral-500';
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const activeCompanies = companies.filter(c => c.status === 'active');

  const columns = useMemo(() => [
    {
      id: "name",
      header: "Company",
      accessorFn: (row: HoldingsCompany) => row.name,
      cell: ({ row }: { row: { original: HoldingsCompany } }) => (
        <Link to={`/holdings/portfolio/${row.original.id}`} className="group block py-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-neutral-900 group-hover:underline decoration-neutral-400 decoration-dotted underline-offset-2">
              {row.original.name}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(row.original.status)}`}>
              {row.original.status.charAt(0).toUpperCase() + row.original.status.slice(1)}
            </span>
          </div>
          {row.original.industry && (
            <div className="text-sm text-neutral-500">{row.original.industry}</div>
          )}
        </Link>
      )
    },
    {
      id: "ownership",
      header: "Ownership",
      accessorFn: (row: HoldingsCompany) => row.ownershipStakePercentage,
      cell: ({ row }: { row: { original: HoldingsCompany } }) => (
        <span className="text-neutral-700">
          {row.original.ownershipStakePercentage
            ? `${row.original.ownershipStakePercentage.toFixed(1)}%`
            : '-'}
        </span>
      )
    },
    {
      id: "people",
      header: "People",
      cell: ({ row }: { row: { original: HoldingsCompany } }) => {
        const peopleCount = row.original.people?.length || 0;
        return (
          <div className="flex items-center gap-1.5 text-neutral-500">
            <Users className="w-3.5 h-3.5" />
            <span>{peopleCount}</span>
          </div>
        );
      }
    },
    {
      id: "acquisitionDate",
      header: "Acquired",
      accessorFn: (row: HoldingsCompany) => row.acquisitionDate,
      cell: ({ row }: { row: { original: HoldingsCompany } }) => (
        <span className="text-neutral-500 text-sm">
          {formatDate(row.original.acquisitionDate)}
        </span>
      )
    },
    {
      id: "website",
      header: "Website",
      accessorFn: (row: HoldingsCompany) => row.website,
      cell: ({ row }: { row: { original: HoldingsCompany } }) => (
        row.original.website ? (
          <a
            href={row.original.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {new URL(row.original.website).hostname.replace('www.', '')}
          </a>
        ) : (
          <span className="text-neutral-400">-</span>
        )
      )
    },
    {
      id: "actions",
      header: "",
      size: 50,
      cell: () => (
        <div className="flex justify-end">
          <ChevronRight className="w-4 h-4 text-neutral-400" />
        </div>
      )
    }
  ], []);

  const { table, ...tableProps } = useTable({
    data: companies,
    columns,
    pagination,
    onPaginationChange: setPagination,
    rowCount: companies.length,
    resourceName: (plural) => `compan${plural ? "ies" : "y"}`,
  });

  const handleFormClose = () => {
    setShowForm(false);
    setEditingCompany(null);
  };

  const handleFormSaved = () => {
    handleFormClose();
    loadCompanies();
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Portfolio Companies</h1>
        <p className="text-neutral-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Portfolio Companies</h1>
          <p className="text-neutral-500">Manage your portfolio company directory</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          icon={<Plus className="w-4 h-4" />}
          text="Add Company"
        />
      </div>

      {/* Summary Stats */}
      {companies.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Active Companies"
            value={activeCompanies.length.toString()}
            hint={`${companies.length} total`}
          />
          <StatCard
            label="Inactive"
            value={companies.filter(c => c.status === 'inactive').length.toString()}
            hint="Companies"
          />
          <StatCard
            label="Sold"
            value={companies.filter(c => c.status === 'sold').length.toString()}
            hint="Exits"
          />
        </div>
      )}

      {/* Companies Table */}
      {companies.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Companies Yet</h3>
            <p className="text-neutral-500 mb-6">Add your first portfolio company to get started.</p>
            <Button
              onClick={() => setShowForm(true)}
              text="Add Company"
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table {...tableProps} table={table} />
        </Card>
      )}

      {/* Company Form Modal */}
      {showForm && (
        <CompanyForm
          company={editingCompany}
          onClose={handleFormClose}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
